import React, { useState } from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { sendFooterEmail } from '../../services/emailService';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [footerEmail, setFooterEmail] = useState<string>('');
  const [isSubmittingFooter, setIsSubmittingFooter] = useState(false);
  const [submitStatusFooter, setSubmitStatusFooter] = useState<{ success: boolean; message: string } | null>(null);

  const handleFooterEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFooterEmail(e.target.value);
  };

  const handleFooterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatusFooter(null);

    // Simple email validation
    if (!footerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(footerEmail)) {
      setSubmitStatusFooter({
        success: false,
        message: t('footer.contact.emailInvalid') || 'Please enter a valid email address.'
      });
      return;
    }

    setIsSubmittingFooter(true);

    try {
      const result = await sendFooterEmail({ email: footerEmail });
      setSubmitStatusFooter(result);

      if (result.success) {
        setFooterEmail(''); // Clear input on success
      }
    } catch (error) {
      console.error('Error submitting footer email:', error);
      setSubmitStatusFooter({
        success: false,
        message: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsSubmittingFooter(false);
    }
  };

  return (
    <footer className="footer-custom-bg text-white pt-5 pb-3">
      <Container>
        <Row>
          <Col md={4} className="mb-4 mb-md-0">
            <h5 className="fw-bold text-white mb-3">{t('footer.about.title')}</h5>
            <Link to="/about#who-are-we" className="footer-link d-block">{t('footer.about.whoAreWe')}</Link>
            <Link to="/about#company-history" className="footer-link d-block">{t('footer.about.history')}</Link>
            <Link to="/about#vision" className="footer-link d-block">{t('footer.about.vision')}</Link>
            <Link to="/about#mission" className="footer-link d-block">{t('footer.about.mission')}</Link>
          </Col>
          <Col md={4} className="mb-4 mb-md-0">
            <h5 className="fw-bold text-white mb-3">{t('footer.services.title')}</h5>
            <Link to="/services#international-freight" className="footer-link d-block">{t('footer.services.service1')}</Link>
            <Link to="/services#heavy-cargo" className="footer-link d-block">{t('footer.services.service2')}</Link>
            <Link to="/services#customs-clearance" className="footer-link d-block">{t('footer.services.service3')}</Link>
            <Link to="/services#packaging" className="footer-link d-block">{t('footer.services.service4')}</Link>
            <Link to="/services#warehouse" className="footer-link d-block">{t('footer.services.service5')}</Link>
          </Col>
          <Col md={4}>
            <h5 className="fw-bold text-white mb-3">{t('footer.contact.title')}</h5>
            <div className="footer-link">{t('footer.contact.phone')}</div>
            <a href="mailto:info@rltransports.biz" className="footer-link d-block">{t('footer.contact.email')}</a>
            <a href="https://www.rltransports.com" target="_blank" rel="noopener noreferrer" className="footer-link d-block">{t('footer.contact.website')}</a>
            <div className="footer-link">{t('footer.contact.address')}</div>
          </Col>
        </Row>
        <Row>
          <Col md={4} className="mb-4 mb-md-0">
            <div className="mt-5 pt-3">
              <Link to="/" className="text-decoration-none">
                <img src="/images/rl-logo.png" alt="R . L Co., Ltd" height={48} className="mb-2" />
              </Link>
              <div className="footer-brand-text">©Powered by: hmwebix.tech</div>
            </div>
          </Col>
          <Col md={4} className="mb-4 mb-md-0">
            <div className="d-flex align-items-center gap-3 mt-5 pt-3">
              <a href="https://www.facebook.com/profile.php?id=100064022371105" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <img src="/images/fb-logo.png" alt="Facebook" width={36} height={36} />
              </a>
              <a href="https://t.me/rlcoltd" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
                <img src="/images/telegram_logo.png" alt="Telegram" width={36} height={36} />
              </a>
              <a href="https://linkedin.com/company/rlcoltd" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <img src="/images/linkedin.png" alt="LinkedIn" width={36} height={36} />
              </a>
              <a href="https://instagram.com/rlcoltd" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <img src="/images/ig.png" alt="Instagram" width={36} height={36} />
              </a>
            </div>
            <div className="mt-3 mb-2 footer-follow">{t('footer.social.follow')}</div>
          </Col>
          <Col md={4}>
            <Form className="d-flex align-items-center gap-2 mt-5 pt-3" onSubmit={handleFooterSubmit}>
              <Form.Control 
                type="email" 
                placeholder={t('footer.contact.emailPlaceholder')} 
                className="footer-email-input py-2" 
                value={footerEmail}
                onChange={handleFooterEmailChange}
                disabled={isSubmittingFooter}
              />
              <Button type="submit" className="footer-submit-btn" disabled={isSubmittingFooter}>
                {isSubmittingFooter ? t('footer.contact.subscribing') : t('footer.contact.submit')}
              </Button>
            </Form>
            {submitStatusFooter && (
              <div className={`mt-2 ${submitStatusFooter.success ? 'text-success' : 'text-danger'}`} style={{ fontSize: '12px' }}>
                {t(submitStatusFooter.message)}
              </div>
            )}
            <div className="mt-2 footer-news-msg">{t('footer.contact.message')}</div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer;